package com.evoapps.evoshift.ui.checklists

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.ui.theme.animateItemIfMotion
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.NoteAdd
import androidx.compose.material.icons.filled.EditNote
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.Checklists
import com.evoapps.evoshift.domain.model.ShiftChecklist
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.CentredLazyColumn
import com.evoapps.evoshift.ui.adaptive.ContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.util.dayAndDate
import com.evoapps.evoshift.viewmodels.ChecklistNoteDraft
import com.evoapps.evoshift.viewmodels.ShiftChecklistViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.checklist_add_note
import evoshift.composeapp.generated.resources.checklist_edit_note
import evoshift.composeapp.generated.resources.checklist_none_on_shift
import evoshift.composeapp.generated.resources.checklist_note
import evoshift.composeapp.generated.resources.checklist_note_required
import evoshift.composeapp.generated.resources.checklist_only_assignee
import evoshift.composeapp.generated.resources.checklist_shift_line
import evoshift.composeapp.generated.resources.checklists_title
import evoshift.composeapp.generated.resources.save
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

/** Test tags of the screen's fixed parts. */
object ShiftChecklistTags {
    const val SHIFT = "shift_checklist_shift"
    const val NOTE_FIELD = "checklist_note_field"
    fun noteButton(checklistId: String, itemId: String) = "checklist_note_${checklistId}_$itemId"
}

/**
 * SH-121: the checklists of one of the person's shifts (from the dashboard card or a reminder):
 * tick the tasks, add the notes some of them need. Read only for anyone but the shift's person.
 */
@Composable
fun ShiftChecklistScreen(viewModel: ShiftChecklistViewModel, onNavigateBack: () -> Unit) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbar = remember { SnackbarHostState() }
    val error = state.error?.asString()
    LaunchedEffect(error) {
        if (error != null) {
            snackbar.showSnackbar(error)
            viewModel.messageShown()
        }
    }

    Scaffold(
        containerColor = Cream,
        snackbarHost = { SnackbarHost(snackbar) },
        topBar = { EvoTopBar(title = stringResource(Res.string.checklists_title), onBack = onNavigateBack) }
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize()) {
            if (state.isLoading) {
                CircularProgressIndicator(Modifier.align(Alignment.Center), color = DeepNavy)
                return@Box
            }
            CentredLazyColumn(
                maxWidth = ContentMaxWidth,
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                state.shift?.let { shift ->
                    item("shift") {
                        val date = runCatching { LocalDate.parse(shift.date) }.getOrNull()
                        Text(
                            stringResource(
                                Res.string.checklist_shift_line,
                                date?.let { dayAndDate(it) } ?: shift.date,
                                Checklists.hourOf(shift.plannedTime.start),
                                Checklists.hourOf(shift.plannedTime.end)
                            ),
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = DeepNavy,
                            modifier = Modifier.testTag(ShiftChecklistTags.SHIFT)
                        )
                    }
                }
                if (!state.canTick && state.checklists.isNotEmpty()) {
                    item("read-only") {
                        Text(stringResource(Res.string.checklist_only_assignee), style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.7f))
                    }
                }
                if (state.checklists.isEmpty()) {
                    item("empty") {
                        Text(stringResource(Res.string.checklist_none_on_shift), style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.7f))
                    }
                }
                items(state.checklists, key = { it.id }) { checklist ->
                    Box(Modifier.animateItemIfMotion(this), propagateMinConstraints = true) {
                        ChecklistCard(
                            checklist = checklist,
                            canTick = state.canTick,
                            onToggle = { item -> viewModel.toggle(checklist, item) },
                            onNote = { item -> viewModel.editNote(checklist, item) }
                        )
                    }
                }
            }
        }
    }

    state.noteDraft?.let { draft ->
        NoteDialog(draft, onChange = viewModel::updateNote, onSave = viewModel::saveNote, onDismiss = viewModel::dismissNote)
    }
}

@Composable
private fun ChecklistCard(
    checklist: ShiftChecklist,
    canTick: Boolean,
    onToggle: (com.evoapps.evoshift.domain.model.ChecklistItem) -> Unit,
    onNote: (com.evoapps.evoshift.domain.model.ChecklistItem) -> Unit
) {
    RegularCard(Modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            ChecklistHeader(checklist, Modifier.padding(bottom = 4.dp))
            ChecklistSkippedLine(checklist)
            checklist.items.forEach { item ->
                ChecklistItemRow(
                    checklist = checklist,
                    item = item,
                    onToggle = if (canTick) { { onToggle(item) } } else null,
                    trailing = if (canTick) {
                        {
                            val hasNote = checklist.noteOf(item.id) != null
                            IconButtonWithTooltip(onClick = { onNote(item) }, modifier = Modifier.handCursor().testTag(ShiftChecklistTags.noteButton(checklist.id, item.id)), tooltip = stringResource(if (hasNote) Res.string.checklist_edit_note else Res.string.checklist_add_note)) {
                                Icon(
                                    if (hasNote) Icons.Default.EditNote else Icons.AutoMirrored.Filled.NoteAdd,
                                    contentDescription = stringResource(if (hasNote) Res.string.checklist_edit_note else Res.string.checklist_add_note),
                                    tint = DeepNavy.copy(alpha = 0.7f)
                                )
                            }
                        }
                    } else null
                )
            }
        }
    }
}

/** The note of a task: what was counted, measured or noticed. Saving it ticks the task. */
@Composable
private fun NoteDialog(draft: ChecklistNoteDraft, onChange: (String) -> Unit, onSave: () -> Unit, onDismiss: () -> Unit) {
    EvoDialog(
        title = draft.item.text,
        onDismiss = onDismiss,
        width = DialogWidth.Compact,
        confirm = DialogAction(stringResource(Res.string.save), onSave, enabled = !draft.item.requiresNote || draft.note.isNotBlank())
    ) {
        EvoTextField(
            value = draft.note,
            onValueChange = { onChange(it.take(Checklists.NOTE_MAX_LENGTH)) },
            label = stringResource(Res.string.checklist_note),
            supportingText = if (draft.item.requiresNote) stringResource(Res.string.checklist_note_required) else null,
            singleLine = false,
            minLines = 2,
            modifier = Modifier.testTag(ShiftChecklistTags.NOTE_FIELD)
        )
    }
}
